import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Background from "@/components/Background";
import ErrorBoundary from "@/components/ErrorBoundary";
import CommandPalette from "@/components/CommandPalette";
import IdleTimeout from "@/components/IdleTimeout";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  House, Table, Plus, SignOut, DotsThree, MagnifyingGlass,
  Envelope, ChartLine, CalendarBlank, Archive, ClockCounterClockwise, Gear, Trash,
  CurrencyDollar,
} from "@phosphor-icons/react";

const primaryTabs = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "tab-dashboard" },
  { to: "/view", label: "Clients", icon: Table, testid: "tab-view" },
  { to: "/add", label: "Add", icon: Plus, testid: "tab-add" },
  { to: "/tasks", label: "Tasks", icon: CalendarBlank, testid: "tab-tasks" },
  { to: "/reports", label: "Reports", icon: ChartLine, testid: "tab-reports" },
];

const moreTabs = [
  { to: "/reports/commissions", label: "Commissions", icon: CurrencyDollar, testid: "menu-commissions" },
  { to: "/emails", label: "Email History", icon: Envelope, testid: "menu-emails" },
  { to: "/activity", label: "Activity Log", icon: ClockCounterClockwise, testid: "menu-activity" },
  { to: "/archive", label: "Archived Clients", icon: Archive, testid: "menu-archive" },
  { to: "/trash", label: "Deleted Clients", icon: Trash, testid: "menu-trash" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "menu-settings" },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global Cmd+K / Ctrl+K shortcut. Also supports "/" as a quick-open when the
  // focus isn't inside an input, matching most modern web apps.
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target?.tagName || "").toLowerCase();
        const editable = e.target?.isContentEditable;
        if (tag === "input" || tag === "textarea" || tag === "select" || editable) return;
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen">
        <Background />
        <header className="sticky top-0 z-40 glass">
          <div className="max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/inspire-logo.png"
                alt="Inspire Education & Migration Services"
                className="w-11 h-11 object-contain shrink-0"
                data-testid="brand-logo"
              />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Client Database</p>
                <h1 className="font-heading font-semibold text-sm md:text-base truncate">
                  Inspire Education & Migration Services
                </h1>
              </div>
            </div>

            <nav className="flex items-center gap-1 bg-slate-200/60 dark:bg-white/5 p-1 rounded-full">
              {primaryTabs.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition ${
                      isActive
                        ? "bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-white"
                        : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                    }`
                  }
                >
                  <Icon size={16} weight="duotone" />
                  <span className="hidden md:inline">{label}</span>
                </NavLink>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="more-menu-trigger"
                    className="flex items-center gap-2 px-3.5 py-2 rounded-full text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                  >
                    <DotsThree size={18} weight="bold" />
                    <span className="hidden md:inline">More</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                  {moreTabs.map(({ to, label, icon: Icon, testid }) => (
                    <DropdownMenuItem
                      key={to}
                      data-testid={testid}
                      onSelect={() => navigate(to)}
                      className="gap-2.5 py-2 cursor-pointer"
                    >
                      <Icon size={16} weight="duotone" /> {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="cmdk-open-btn"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPaletteOpen(true)}
                    className="rounded-full"
                  >
                    <MagnifyingGlass size={18} weight="duotone" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Search (⌘K / Ctrl K)
                </TooltipContent>
              </Tooltip>
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-black/10 dark:border-white/10" />
              ) : null}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="logout-btn" variant="ghost" size="icon"
                    onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                    className="rounded-full"
                  >
                    <SignOut size={18} weight="duotone" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Sign out</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </header>

        <main className="max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1920px] mx-auto p-6 md:p-10">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <IdleTimeout />
      </div>
    </TooltipProvider>
  );
};

export default Layout;
